import LoadingSpinner from './LoadingSpinner';
import React from 'react';
import WebsiteCard from './WebsiteCard';
import { Brain, Search, XCircle } from 'lucide-react';

function Dashboard({ 
  websites, 
  onEditWebsite, 
  onDeleteWebsite, 
  onToggleWebsite,
  onScanWebsite,
  canActivateWebsite,
  searchTerm, 
  appliedSearchTerm,
  filter = 'all',
  setSearchTerm,
  onSearchNow,
  maxIntervalCheck,
  hasAIFeatures,
  loading,
  activeCountTotal,
  error,
  onRetry,
  user,
  darkMode = false
}) {
  const redColor = '#FD0E0B';
  const lightRed = '#FF6B6B';
  const greenColor = '#95DE1A';

  const normalizedSearch = (appliedSearchTerm ?? searchTerm).trim().toLowerCase();
  const filteredWebsites = websites.filter(website => {
    const isService = website.type === 'SERVICE';
    const isBlacklist = website.type === 'BLACKLIST';
    const matchesFilter = filter === 'all'
      || (filter === 'online' && !isService && !isBlacklist && website.isActive && website.status === 'up')
      || (filter === 'offline' && !isService && !isBlacklist && website.isActive && website.status === 'down')
      || (filter === 'unknown' && !isService && !isBlacklist && website.isActive && !['up', 'down'].includes(website.status))
      || (filter === 'inactive' && !website.isActive)
      || (filter === 'service' && isService)
      || (filter === 'blacklist' && isBlacklist);
    if (!matchesFilter) return false;
    if (!normalizedSearch) return true;
    const text = [website.name, website.url, website.target, website.type, website.status]
      .filter(Boolean).join(' ').toLowerCase();
    if (normalizedSearch === 'active') return Boolean(website.isActive);
    if (normalizedSearch === 'inactive') return !website.isActive;
    if (['online', 'up'].includes(normalizedSearch)) return website.status === 'up';
    if (['offline', 'down'].includes(normalizedSearch)) return website.status === 'down';
    return text.includes(normalizedSearch)
      || (normalizedSearch === 'listed' && website.status === 'listed')
      || (normalizedSearch === 'clean' && website.status === 'clean')
      || (normalizedSearch === 'blacklist' && isBlacklist);
  });

  const activeWebsites = filteredWebsites.filter(w => w.isActive);
  const inactiveWebsites = filteredWebsites.filter(w => !w.isActive);
  const activeCount = activeCountTotal ?? activeWebsites.length;
  const maxWebsites = user?.maxWebsites || 3;
  const userPlan = user?.planType?.toLowerCase() || 'free';

  return (
    <div>
      <div className="mb-6 w-full">
        <div className="relative max-w-3xl mx-auto lg:mx-0 lg:max-w-full">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            aria-label="Search monitors"
            className={`block w-full pl-10 pr-3 py-3 border rounded-xl leading-5 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-150 ease-in-out sm:text-sm ${
              darkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            placeholder="Search by name, URL, status (up/down/unknown/listed/clean) or 'blacklist'..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onSearchNow?.();
            }}
          />
        </div>
      </div>

      {loading && websites.length === 0 && (
        <div className="py-8"><LoadingSpinner darkMode={darkMode} /></div>
      )}
      {!loading && websites.length === 0 && !searchTerm.trim() && !error && (
        <p className={`py-8 text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          No websites to monitor yet. Add a monitor to get started.
        </p>
      )}

      {!loading && !error && filteredWebsites.length === 0 && (websites.length > 0 || searchTerm.trim() || filter !== 'all') && (
        <div className={`rounded-2xl border px-6 py-10 text-center ${darkMode ? 'border-gray-700 bg-gray-800 text-gray-200' : 'border-gray-200 bg-white text-gray-700'}`} role="status">
          <Search className="mx-auto mb-3 h-8 w-8 text-gray-400" aria-hidden="true" />
          <h3 className="text-lg font-semibold">No monitors match the current search or filter</h3>
          <p className="mt-2 text-sm text-gray-500">Try another name, URL, status, or monitor type.</p>
        </div>
      )}

      {error && (
        <div
          className={`rounded-2xl border px-6 py-10 text-center ${
            darkMode ? 'border-gray-700 bg-gray-800 text-gray-200' : 'border-gray-200 bg-white text-gray-700'
          }`}
          role="status"
        >
          <Search className="mx-auto mb-3 h-8 w-8 text-gray-400" aria-hidden="true" />
          <h3 className="text-lg font-semibold">Could not load monitors</h3>
          <p className="mt-2 text-sm text-gray-500">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            <XCircle className="mr-2 h-4 w-4" aria-hidden="true" />
            Retry
          </button>
        </div>
      )}

      {filteredWebsites.length > 0 && <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Monitor Status</h2>
            <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
              Showing <span className="font-semibold" style={{ color: greenColor }}>{filteredWebsites.length}</span> monitors; <span className="font-semibold" style={{ color: greenColor }}>{activeCount}</span> active total
              {inactiveWebsites.length > 0 && (
                <span className="text-red-600"> ({inactiveWebsites.length} inactive)</span>
              )}
              {hasAIFeatures && (
                <span 
                  className="ml-2 inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: `${lightRed}40`, color: redColor }}
                >
                  <Brain className="h-3 w-3" style={{ color: redColor }} />
                  <span>AI plan feature</span>
                </span>
              )}
            </p>
          </div>
          <div className="hidden md:flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-sm">
              <div 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: redColor }}
              ></div>
              <span style={{ color: redColor, fontWeight: 500 }}>
                Live monitoring active 
              </span>
              <span style={{ color: greenColor, fontWeight: 500 }}>
                (checks every {
                  maxIntervalCheck >= 60
                    ? `${Math.floor(maxIntervalCheck / 60)}min`
                    : `${maxIntervalCheck}sec`
                })
              </span>
            </div>
          </div>
        </div>
      </div>}

      {activeWebsites.length > 0 && (
        <div className="mb-8">
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Active Monitoring</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {activeWebsites.map((website) => (
              <WebsiteCard
                key={website.id}
                website={website}
                onEdit={() => onEditWebsite(website)}
                onDelete={() => onDeleteWebsite(website.id)}
                onToggle={(id, isActive) => onToggleWebsite(id, isActive)}
                onScan={onScanWebsite}
                canActivateWebsite={canActivateWebsite}
                activeCount={activeCount}
                maxWebsites={maxWebsites}
                darkMode={darkMode}
                userPlan={userPlan}
              />
            ))}
          </div>
        </div>
      )}

      {inactiveWebsites.length > 0 && (
        <div>
          <h3 className={`text-lg font-semibold mb-4 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Inactive Websites
            <span 
              className="ml-2 px-2 py-1 text-xs rounded-full"
              style={{ backgroundColor: `${lightRed}40`, color: redColor }}
            >
              Plan limited or manually paused
            </span>
          </h3>
          <div 
            className="rounded-2xl p-4 mb-6"
            style={{ backgroundColor: `${lightRed}20`, border: `1px solid ${lightRed}60` }}
          >
            <p className="text-sm" style={{ color: redColor }}>
              <strong>Note:</strong> Inactive monitors may be paused manually or by your plan limits. You can reactivate an individual monitor when your plan allows it.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {inactiveWebsites.map((website) => (
              <WebsiteCard
                key={website.id}
                website={website}
                onEdit={() => onEditWebsite(website)}
                onDelete={() => onDeleteWebsite(website.id)}
                onToggle={(id, isActive) => onToggleWebsite(id, isActive)}
                onScan={onScanWebsite}
                canActivateWebsite={canActivateWebsite}
                activeCount={activeCount}
                maxWebsites={maxWebsites}
                darkMode={darkMode}
                userPlan={userPlan}
              />
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

export default Dashboard;
